import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../components/layout/PageLayout';
import { clothingService, ClothingItem } from '../services/clothing';
import { outfitService } from '../services/outfit';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Carousel } from '../components/common/Carousel';
import { OutfitPreview } from '../components/common/OutfitPreview';
import { UploadModal } from '../components/common/UploadModal';
import { storage } from '../config/firebase';
import { ref, getDownloadURL } from 'firebase/storage';

// Category mapping to normalize category names
const CATEGORY_MAP: { [key: string]: string } = {
  't-shirt': 'top',
  'shirt': 'top',
  'blouse': 'top',
  'sweater': 'top',
  'hoodie': 'top',
  'tank top': 'top',
  'pants': 'bottom',
  'jeans': 'bottom',
  'shorts': 'bottom',
  'skirt': 'bottom',
  'dress': 'dress',
  'jumpsuit': 'dress',
  'jacket': 'outerwear',
  'coat': 'outerwear',
  'blazer': 'outerwear',
  'sneakers': 'shoes',
  'boots': 'shoes',
  'sandals': 'shoes',
  'heels': 'shoes',
  'hat': 'hat',
  'bag': 'accessory',
  'belt': 'accessory',
  'jewelry': 'accessory',
};

// Define the order and display names for categories
const CATEGORIES = [
  { key: 'hat', label: 'Hat' },
  { key: 'accessory', label: 'Accessory' },
  { key: 'outerwear', label: 'Outerwear' },
  { key: 'top', label: 'Top' },
  { key: 'dress', label: 'Dress' },
  { key: 'bottom', label: 'Bottom' },
  { key: 'shoes', label: 'Shoes' },
];

const OCCASION_OPTIONS = [
  '',
  'Wedding',
  'Formal event',
  'Date night',
  'Thanksgiving',
  'Christmas',
  'Family photos',
  'Office Outfit',
  'Everyday Casual',
  'Other'
];

export const CreateOutfit: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [occasion, setOccasion] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [items, setItems] = useState<ClothingItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<{ [category: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [generatedPreviewUrl, setGeneratedPreviewUrl] = useState<string | null>(null);
  const [resolvedPreviewUrl, setResolvedPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      console.log('Loading clothing items...');
      const data = await clothingService.getAll();
      console.log(`Successfully loaded ${data.length} clothing items`);
      setItems(data);
    } catch (err: any) {
      console.error('Failed to load items:', err);
      setError(err.response?.data?.error || 'Failed to load clothing items. Please refresh the page.');
    }
  };

  // Group items by normalized category
  const itemsByCategory = useMemo(() => {
    try {
      const grouped: { [category: string]: ClothingItem[] } = {};
      
      items.forEach((item) => {
        try {
          if (!item || !item.category) {
            console.warn('Invalid item found:', item);
            return;
          }
          const normalizedCategory = CATEGORY_MAP[item.category.toLowerCase()] || item.category.toLowerCase();
          if (!grouped[normalizedCategory]) {
            grouped[normalizedCategory] = [];
          }
          grouped[normalizedCategory].push(item);
        } catch (err) {
          console.error('Error processing item:', err, item);
        }
      });

      console.log('Items grouped by category:', Object.keys(grouped).map(k => `${k}: ${grouped[k].length}`));
      return grouped;
    } catch (err) {
      console.error('Error grouping items by category:', err);
      return {};
    }
  }, [items]);

  // Auto-select items when there's only one item in a category
  useEffect(() => {
    if (items.length > 0 && Object.keys(itemsByCategory).length > 0) {
      setSelectedItems((prev) => {
        const newSelections: { [category: string]: string } = { ...prev };
        let hasChanges = false;

        CATEGORIES.forEach((category) => {
          const categoryItems = itemsByCategory[category.key] || [];
          // Auto-select if there's exactly one item and it's not already selected
          if (categoryItems.length === 1 && !newSelections[category.key]) {
            newSelections[category.key] = categoryItems[0].id;
            hasChanges = true;
          }
        });

        return hasChanges ? newSelections : prev;
      });
    }
  }, [itemsByCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCategoryChange = (category: string, itemId: string) => {
    try {
      console.log(`Category ${category} changed to item: ${itemId}`);
      setSelectedItems((prev) => ({
        ...prev,
        [category]: itemId || '',
      }));
      // Clear generated preview when selection changes
      setGeneratedPreviewUrl(null);
    } catch (err) {
      console.error('Error handling category change:', err);
      setError('Failed to update selection. Please try again.');
    }
  };

  const handleGeneratePreview = async () => {
    const selectedItemIds = Object.values(selectedItems).filter((id) => id !== '');
    
    if (selectedItemIds.length === 0) {
      setError('Please select at least one item');
      return;
    }

    setGeneratingPreview(true);
    setError('');
    setGeneratedPreviewUrl(null);
    setResolvedPreviewUrl(null);

    try {
      console.log('Generating outfit preview with items:', selectedItemIds);
      const result = await outfitService.generatePreview(selectedItemIds);
      console.log('Preview generated:', result.preview_url);
      setGeneratedPreviewUrl(result.preview_url);
      
      // Resolve the URL using Firebase Storage SDK for proper authentication
      const previewUrl = result.preview_url;
      if (previewUrl) {
        try {
          // Extract file path from URL
          // Format: http://localhost:9199/v0/b/bucket/o/path?alt=media
          // or: https://storage.googleapis.com/bucket/path
          let filePath: string | null = null;
          
          if (previewUrl.includes('/o/')) {
            // Emulator or Firebase Storage URL format
            const match = previewUrl.match(/\/o\/([^?]+)/);
            if (match) {
              filePath = decodeURIComponent(match[1]);
            }
          } else if (previewUrl.includes('storage.googleapis.com')) {
            // Production URL format: https://storage.googleapis.com/bucket/path
            const match = previewUrl.match(/storage\.googleapis\.com\/[^/]+\/(.+)/);
            if (match) {
              filePath = match[1];
            }
          }
          
          if (filePath) {
            console.log('Extracted file path:', filePath);
            // Get download URL using Firebase Storage SDK
            const storageRef = ref(storage, filePath);
            const downloadURL = await getDownloadURL(storageRef);
            console.log('Resolved download URL:', downloadURL);
            setResolvedPreviewUrl(downloadURL);
          } else {
            // Fallback to original URL
            console.warn('Could not extract file path, using original URL');
            setResolvedPreviewUrl(previewUrl);
          }
        } catch (urlError) {
          console.error('Failed to resolve preview URL:', urlError);
          // Fallback to original URL
          setResolvedPreviewUrl(previewUrl);
        }
      }
    } catch (err: any) {
      console.error('Failed to generate preview:', err);
      // Firebase Functions errors have code and message properties
      let errorMessage = 'Failed to generate outfit preview.';
      if (err.code) {
        // Firebase Functions HttpsError
        errorMessage = err.message || err.code;
      } else if (err.response?.data?.error) {
        // Regular HTTP error
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      if (errorMessage.includes('profile picture') || errorMessage.includes('Please upload a profile picture')) {
        setError('Please upload a profile picture first from your Dashboard.');
      } else if (errorMessage.includes('not support image generation') || errorMessage.includes('unimplemented')) {
        setError('Image generation is currently not available. Please use the silhouette preview instead.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setGeneratingPreview(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate outfit name is required
    if (!name.trim()) {
      setError('Outfit name is required');
      return;
    }
    
    const selectedItemIds = Object.values(selectedItems).filter((id) => id !== '');
    
    if (selectedItemIds.length === 0) {
      setError('Please select at least one item');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await outfitService.create({
        name: name.trim(),
        items: selectedItemIds,
        is_public: isPublic,
        occasion: occasion || undefined,
      });
      navigate('/outfits');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create outfit');
    } finally {
      setLoading(false);
    }
  };

  // Get selected items for preview
  const previewItems = useMemo(() => {
    try {
      const preview: { [category: string]: ClothingItem | null } = {};
      Object.entries(selectedItems).forEach(([category, itemId]) => {
        try {
          if (itemId) {
            const item = items.find((i) => i.id === itemId);
            if (item) {
              preview[category] = item;
            } else {
              console.warn(`Item not found for ID ${itemId} in category ${category}`);
            }
          }
        } catch (err) {
          console.error(`Error processing preview item for category ${category}:`, err);
        }
      });
      return preview;
    } catch (err) {
      console.error('Error building preview items:', err);
      return {};
    }
  }, [selectedItems, items]);

  const selectedCount = Object.values(selectedItems).filter((id) => id !== '').length;

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-3xl shadow-lg p-8">
          <div className="flex justify-between items-start mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Create Outfit</h1>
            <button
              onClick={() => navigate('/outfits')}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left side: Form with category dropdowns */}
            <div>
          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              label="Outfit Name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Casual Friday"
              required
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Occasion <span className="text-gray-500 font-normal">(Optional)</span>
              </label>
              <select
                value={occasion}
                onChange={(e) => setOccasion(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {OCCASION_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt || 'Select an occasion...'}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <input
                type="checkbox"
                id="isPublic"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-primary"
              />
              <label htmlFor="isPublic" className="flex-1 cursor-pointer">
                <span className="text-sm font-medium text-gray-700">Make this outfit public</span>
                <p className="text-xs text-gray-500 mt-1">
                  Public outfits will be visible to everyone in the Community feed
                </p>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select Items by Category ({selectedCount} selected)
              </label>
              {items.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No items in your closet. <button onClick={() => setUploadModalOpen(true)} className="text-primary underline">Upload some items</button> first.
                </p>
              ) : (
                    <div className="space-y-6">
                      {CATEGORIES.map((category) => {
                        const categoryItems = itemsByCategory[category.key] || [];
                        
                        if (categoryItems.length === 0) {
                          return null;
                        }

                        const carouselItems = categoryItems.map((item) => ({
                          id: item.id,
                          image_url: item.image_url,
                          label: `${item.subcategory || item.category}${item.color ? ` (${item.color})` : ''}`,
                        }));

                    return (
                          <div key={category.key}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {category.label}
                              {categoryItems.length === 1 && (
                                <span className="ml-2 text-xs text-gray-500 font-normal">(auto-selected)</span>
                              )}
                            </label>
                            <Carousel
                              items={carouselItems}
                              selectedId={selectedItems[category.key]}
                              onSelect={(id) => handleCategoryChange(category.key, id)}
                            />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

                {selectedCount > 0 && (
                  <div className="pt-4 border-t">
                    <Button
                      type="button"
                      variant="accent"
                      className="w-full"
                      onClick={handleGeneratePreview}
                      disabled={generatingPreview}
                    >
                      {generatingPreview ? 'Generating Preview...' : 'Generate Outfit Preview'}
                    </Button>
                  </div>
                )}

            {error && <p className="text-red-500 text-sm">{error}</p>}

            <div className="flex gap-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/outfits')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                className="flex-1"
                disabled={loading || selectedCount === 0 || !name.trim()}
              >
                {loading ? 'Creating...' : 'Create Outfit'}
              </Button>
            </div>
          </form>
            </div>

            {/* Right side: Outfit preview */}
            <div className="lg:sticky lg:top-8 lg:self-start">
              <div className="bg-gray-50 rounded-2xl p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 text-center">
                  {generatedPreviewUrl ? 'Generated Outfit Preview' : 'Outfit Preview'}
                </h2>
                {generatedPreviewUrl ? (
                  <div className="space-y-4">
                    <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-white border-2 border-gray-200">
                      {resolvedPreviewUrl ? (
                        <img
                          src={resolvedPreviewUrl}
                          alt="Generated outfit preview"
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            console.error('❌ Failed to load generated preview');
                            console.error('   Original URL:', generatedPreviewUrl);
                            console.error('   Resolved URL:', resolvedPreviewUrl);
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                          onLoad={() => {
                            console.log('✅ Preview image loaded successfully');
                            console.log('   URL:', resolvedPreviewUrl);
                          }}
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-2"></div>
                            <p className="text-sm text-gray-600">Loading preview...</p>
                          </div>
                        </div>
                      )}
                      {generatingPreview && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/80">
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-2"></div>
                            <p className="text-sm text-gray-600">Generating preview...</p>
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 text-center">
                      AI-generated preview showing you wearing the selected outfit
                    </p>
                  </div>
                ) : selectedCount === 0 ? (
                  <div className="flex items-center justify-center h-96 text-gray-400">
                    <p>Select items and click "Generate Outfit Preview" to see yourself wearing the outfit</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <OutfitPreview items={previewItems} />
                    <p className="text-sm text-gray-600 text-center">
                      Click "Generate Outfit Preview" above to see yourself wearing this outfit
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <UploadModal
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onSuccess={loadItems}
      />
    </PageLayout>
  );
};

